// SelectGlobal.tsx
import Select, { Props, GroupBase, Theme } from 'react-select'

const defaultTheme = (base: Theme): Theme => ({
  ...base,
  // Match form controls: 4px radius, 36px height
  borderRadius: 4,
  spacing: { ...base.spacing, baseUnit: 4, controlHeight: 36, menuGutter: 4 },
  colors: {
    ...base.colors,
    primary: 'var(--select-primary)',
    primary25: 'var(--select-primary-25)',
    primary50: 'var(--select-primary-50)',
    neutral0: 'var(--select-bg)',
    neutral5: 'var(--select-neutral-5)',
    neutral10: 'var(--select-neutral-10)',
    neutral20: 'var(--select-border)',
    neutral30: 'var(--select-border-hover)',
    neutral40: 'var(--select-muted)',
    neutral80: 'var(--select-fg)',
  },
})

const defaultStyles = {
  control: (base: any, state: any) => ({
    ...base,
    borderColor: 'var(--select-border)',//state.isFocused ? 'var(--select-primary)' : 'var(--select-border)',
    boxShadow: 'none',//state.isFocused ? '0 0 0 2px var(--select-focus-ring)' : 'none',
    ':hover': { borderColor: 'var(--select-border-hover)' },
    minHeight: 36,
    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
  }),
  menu: (base: any) => ({
    ...base,
    zIndex: 1000,
    border: '1px solid var(--select-border)',
    boxShadow: 'var(--shadow-md)',
  }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected
      ? 'var(--select-primary)'
      : state.isFocused
      ? 'var(--select-primary-25)'
      : 'var(--select-bg)',
    color: state.isSelected ? 'var(--select-on-primary)' : 'var(--select-fg)',
    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
  }),
  multiValue: (base: any) => ({
    ...base,
    backgroundColor: 'var(--select-chip-bg)',
  }),
  multiValueLabel: (base: any) => ({
    ...base,
    color: 'var(--select-chip-fg)',
  }),
  multiValueRemove: (base: any) => ({
    ...base,
    ':hover': { backgroundColor: 'var(--select-chip-remove-bg)' },
  }),
  indicatorSeparator: (base: any) => ({
    ...base,
    backgroundColor: 'var(--select-border)',
  })
}

// simple merge so per-instance styles still work
const mergeStyles = (a: any = {}, b: any = {}) =>
  Object.fromEntries(
    Object.entries({ ...a, ...b }).map(([k, _fn]) => [
      k,
      (...args: any[]) => {
        const fromA = a?.[k]?.(...args)
        const fromB = b?.[k]?.(...args)
        return { ...fromA, ...fromB }
      }
    ])
  )

export default function SelectGlobal<Option, IsMulti extends boolean = false, Group extends GroupBase<Option> = GroupBase<Option>>(
  props: Props<Option, IsMulti, Group>
) {
  const { styles, theme: themeOverride, classNamePrefix, ...rest } = props
  const mergedStyles = mergeStyles(defaultStyles, styles)

  return (
    <Select
      {...rest}
      theme={(base: Theme) => {
        const applied = defaultTheme(base)
        if (!themeOverride) return applied
        return typeof themeOverride === 'function'
          ? themeOverride(applied)
          : { ...applied, ...themeOverride }
      }}
      classNamePrefix={classNamePrefix ?? 'rs'}
      menuPortalTarget={document.body}
      // keep the menu above modals/tooltips if you use portals
      styles={{
        ...mergedStyles,
        menuPortal: (base: any) => ({ ...base, zIndex: 2000 })
      }}
    />
  )
}
